/**
 * Global Bet Slip Manager
 *
 * This module provides centralized state management for bet slips across multiple widget instances.
 * It uses React's useSyncExternalStore for efficient state synchronization.
 *
 * Key Features:
 * - Cross-widget bet deduplication (same selection from same event becomes single bet)
 * - Immutable state updates to ensure React detects changes
 * - Deep link URL generation for each sportsbook
 * - Automatic cleanup when widgets are destroyed
 *
 * Architecture:
 * 1. GlobalBetSlipManager - Singleton class managing all bet slip state
 * 2. useGlobalBetSlip - React hook for components to subscribe to state changes
 * 3. Immutable Updates - All state changes create new object references for React
 */

import type { SportsbookMeta } from '@/api/affiliateLinks'
import type { Sportsbook } from '@/types/types'
import { useSyncExternalStore } from 'react'

interface BetSlipItem {
  id: string
  widgetId: string // Which widget added this bet
  league: string // Which league this bet is for
  eventName: string // Event display name
  marketName: string // Market display name
  selectionName: string
  odds: {
    american: string
    decimal: string
  }
  stake: string
}

interface FanduelBetSlipItem extends BetSlipItem {
  sportsbook: 'FanDuel'
  // selected data is used for generating deep links
  selectedData: {
    marketId: string
    selectionId: string
  }
}

interface DraftKingsBetSlipItem extends BetSlipItem {
  sportsbook: 'DraftKings'
  // selected data is used for generating deep links
  selectedData: {
    selectionId: string
  }
}

interface Bet365BetSlipItem extends BetSlipItem {
  sportsbook: 'Bet365'
  // selected data is used for generating deep links
  selectedData: {
    selectionId: string
  }
}

interface BetMGMBetSlipItem extends BetSlipItem {
  sportsbook: 'BetMGM'
  // selected data is used for generating deep links
  selectedData: {
    optionId: string
    marketId: string
    fixtureId: string
  }
}

interface PolymarketBetSlipItem extends BetSlipItem {
  sportsbook: 'Polymarket'
  // Polymarket isn't a sportsbook with a per-bet deep-link API — affiliate URLs
  // are static OneLink redirects keyed by sport. We still capture the gamma
  // event/market identifiers so future deep-linking can use them.
  selectedData: {
    marketId: string
    marketSlug: string
    eventId: string
    eventSlug: string
  }
}

interface StakeBetSlipItem extends BetSlipItem {
  sportsbook: 'Stake'
  // Stake's odds-data API is read-only — no deep-link target. Affiliate links
  // are sport-level. We capture Stake's synthetic identifiers so future
  // deep-linking work has the data ready.
  selectedData: {
    selectionKey: string
    marketKey: string
    fixtureSlug: string
  }
}

interface UnderdogBetSlipItem extends BetSlipItem {
  sportsbook: 'Underdog'
  // Underdog's Partner API can turn a set of option ids into a partner-
  // attributed deep link (POST /partners/v1/shares) — the option id is the
  // unit that call prices. Today the bet slip routes to the per-sport
  // affiliate URL; the identifiers are captured so slip-share deep-linking
  // can plug in without a payload change.
  selectedData: {
    optionId: string
    lineId: string
    matchKey: string
  }
}

export type GlobalBetSlipItem = FanduelBetSlipItem | DraftKingsBetSlipItem | Bet365BetSlipItem | BetMGMBetSlipItem | PolymarketBetSlipItem | StakeBetSlipItem | UnderdogBetSlipItem

export interface GlobalBetSlipState {
  FanDuel: FanduelBetSlipItem[]
  DraftKings: DraftKingsBetSlipItem[]
  Bet365: Bet365BetSlipItem[]
  BetMGM: BetMGMBetSlipItem[]
  Polymarket: PolymarketBetSlipItem[]
  Stake: StakeBetSlipItem[]
  Underdog: UnderdogBetSlipItem[]
  lastAddedSportsbook: Sportsbook | null // Track which sportsbook was last added to
}

/**
 * Listener function type for React components subscribing to state changes
 */
type GlobalBetSlipListener = () => void

/**
 * Global Bet Slip Manager Class
 *
 * Singleton class that manages bet slip state across all widget instances.
 * Uses the Observer pattern with React's useSyncExternalStore for efficient updates.
 *
 * CRITICAL: All state updates must be immutable to ensure React detects changes.
 * This means creating new object references whenever the state changes.
 */
class GlobalBetSlipManager {
  private state: GlobalBetSlipState = {
    FanDuel: [],
    DraftKings: [],
    Bet365: [],
    BetMGM: [],
    Polymarket: [],
    Stake: [],
    Underdog: [],
    lastAddedSportsbook: null,
  }

  private listeners = new Set<GlobalBetSlipListener>()

  // Per-league sportsbook brand metadata (URL + brand_color + secondary_color
  // + logo_url). Sourced from /events response which joins the Supabase
  // catalog. One entry per league seen so far this session.
  private sportsbookMeta = new Map<string, Record<string, SportsbookMeta>>()

  /**
   * Register sportsbook brand metadata for a league (called when API
   * response arrives). The map is keyed by canonical sportsbook name and
   * each value carries url + brand_color + secondary_color + logo_url.
   */
  setSportsbookMeta = (league: string, meta: Record<string, SportsbookMeta>) => {
    this.sportsbookMeta.set(league, meta)
  }

  /**
   * Get the affiliate URL for a given league and sportsbook. Returns
   * undefined if the API response didn't include this book.
   */
  getSportsbookLink = (league: string, sportsbook: string): string | undefined => {
    return this.sportsbookMeta.get(league)?.[sportsbook]?.url ?? undefined
  }

  /**
   * Get the full brand metadata (URL + colors + logo) for a given league
   * and sportsbook. Returns undefined when missing.
   */
  getSportsbookMeta = (league: string, sportsbook: string): SportsbookMeta | undefined => {
    return this.sportsbookMeta.get(league)?.[sportsbook]
  }

  // React's useSyncExternalStore integration
  /**
   * React's useSyncExternalStore integration
   * Subscribe method adds listeners that get called when state changes
   */
  subscribe = (listener: GlobalBetSlipListener) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * getSnapshot returns the current state for React's useSyncExternalStore
   * IMPORTANT: We return the state object reference directly. React compares
   * this reference using Object.is() to detect changes, which is why we use
   * immutable updates throughout this class.
   */
  getSnapshot = () => this.state

  /**
   * Notify all subscribed React components that state has changed
   * This triggers React to call getSnapshot() and re-render if needed
   */
  private notify = () => {
    this.listeners.forEach(listener => listener())
  }

  /**
   * Helper method to extract selection key from bet data
   * Handles different selectedData structures for different sportsbooks
   */
  private getSelectionKey(bet: Omit<GlobalBetSlipItem, 'id' | 'widgetId'> | GlobalBetSlipItem, sportsbook: Sportsbook): string {
    if (sportsbook === 'BetMGM')
      return (bet.selectedData as { optionId: string }).optionId
    if (sportsbook === 'Polymarket')
      return (bet.selectedData as { marketId: string }).marketId
    if (sportsbook === 'Stake')
      return (bet.selectedData as { selectionKey: string }).selectionKey
    if (sportsbook === 'Underdog')
      return (bet.selectedData as { optionId: string }).optionId
    return (bet.selectedData as { selectionId: string }).selectionId
  }

  /**
   * Helper method to find existing bet index for duplicate detection
   * Returns -1 if no duplicate found
   */
  private findExistingBetIndex(bet: Omit<GlobalBetSlipItem, 'id' | 'widgetId'>, sportsbook: Sportsbook): number {
    const newSelectionKey = this.getSelectionKey(bet, sportsbook)
    return this.state[sportsbook].findIndex((existingBet) => {
      const existingSelectionKey = this.getSelectionKey(existingBet, sportsbook)
      return existingBet.eventName === bet.eventName && existingSelectionKey === newSelectionKey
    })
  }

  /**
   * Add bet from specific widget
   * IMPORTANT: Uses immutable updates to ensure React detects state changes
   * Each state modification creates a new state object reference
   */
  addBet = (widgetId: string, bet: Omit<GlobalBetSlipItem, 'id' | 'widgetId'>) => {
    const sportsbook = bet.sportsbook
    const selectionKey = this.getSelectionKey(bet, sportsbook)
    const betId = `${widgetId}-${bet.eventName}-${selectionKey}-${bet.sportsbook}`

    const newBet: GlobalBetSlipItem = {
      ...bet,
      id: betId,
      widgetId,
    } as GlobalBetSlipItem

    const existingBetIndex = this.findExistingBetIndex(bet, sportsbook)

    if (existingBetIndex !== -1) {
      // Update existing bet (replace with new one from this widget)
      // Preserve the existing stake to maintain user input
      const existingBet = this.state[sportsbook][existingBetIndex]
      const newBets = [...this.state[sportsbook]]
      newBets[existingBetIndex] = { ...newBet, stake: existingBet?.stake || '' }
      this.state = {
        ...this.state,
        [sportsbook]: newBets,
        lastAddedSportsbook: sportsbook,
      }
    }
    else {
      // Add new bet and create new state reference
      // This immutable update ensures React detects the change
      this.state = {
        ...this.state,
        [sportsbook]: [...this.state[sportsbook], newBet],
        lastAddedSportsbook: sportsbook,
      }
    }

    // Notify React components
    this.notify()
  }

  /**
   * Remove bet by ID from specific sportsbook
   * Uses immutable updates to ensure React detects the change
   */
  removeBet = (betId: string, sportsbook: Sportsbook) => {
    const betIndex = this.state[sportsbook].findIndex(bet => bet.id === betId)
    if (betIndex !== -1) {
      // Create new state with bet removed (immutable update)
      this.state = {
        ...this.state,
        [sportsbook]: this.state[sportsbook].filter(bet => bet.id !== betId),
      }
      this.notify()
    }
  }

  /**
   * Update stake for a specific bet in specific sportsbook
   * Uses immutable updates to ensure React detects the change
   */
  updateStake = (betId: string, stake: string, sportsbook: Sportsbook) => {
    const betIndex = this.state[sportsbook].findIndex(b => b.id === betId)
    if (betIndex !== -1) {
      // Create new array with updated bet (immutable update)
      const updatedBets = [...this.state[sportsbook]]
      const existingBet = updatedBets[betIndex]
      if (existingBet) {
        updatedBets[betIndex] = { ...existingBet, stake }

        this.state = {
          ...this.state,
          [sportsbook]: updatedBets,
        }

        this.notify()
      }
    }
  }

  /**
   * Clear all bets for a specific sportsbook
   * Uses immutable updates to ensure React detects the change
   */
  clearSportsbook = (sportsbook: Sportsbook) => {
    this.state = {
      ...this.state,
      [sportsbook]: [],
    }
    this.notify()
  }

  /**
   * Clear all bets from a specific widget across all sportsbooks
   * Uses immutable updates to ensure React detects the change
   */
  clearWidget = (widgetId: string) => {
    // Filter out bets from this widget across all sportsbooks
    for (const sportsbook of ['DraftKings', 'FanDuel', 'Bet365', 'BetMGM', 'Polymarket', 'Stake', 'Underdog'] as Sportsbook[]) {
      this.state = {
        ...this.state,
        [sportsbook]: this.state[sportsbook].filter(bet => bet.widgetId !== widgetId),
      }
    }

    this.notify()
  }

  getBetsBySportsbook = () => {
    return { ...this.state }
  }
}

/**
 * Singleton instance of the GlobalBetSlipManager
 * This single instance is shared across all widget instances and React components
 */
export const globalBetSlipManager = new GlobalBetSlipManager()

/**
 * React hook to use global bet slip state
 * Uses React's useSyncExternalStore for efficient subscription to state changes
 */
export function useGlobalBetSlip() {
  const state = useSyncExternalStore(
    globalBetSlipManager.subscribe,
    globalBetSlipManager.getSnapshot,
  )

  return {
    betSlip: state,
    lastAddedSportsbook: state.lastAddedSportsbook,
    addBet: globalBetSlipManager.addBet,
    removeBet: globalBetSlipManager.removeBet,
    updateStake: globalBetSlipManager.updateStake,
    clearSportsbook: globalBetSlipManager.clearSportsbook,
    clearWidget: globalBetSlipManager.clearWidget,
    getSportsbookLink: globalBetSlipManager.getSportsbookLink,
  }
}
